/**
 * MockBrowserEmbed - Shows what the browser would look like when connected
 * This simulates the VNC connection until we can get real noVNC working
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import type { BrowserSession } from '../types/browser.types';

interface MockBrowserEmbedProps {
  session: BrowserSession;
  className?: string;
  showControls?: boolean;
  showStats?: boolean;
}

const MockBrowserEmbed: React.FC<MockBrowserEmbedProps> = ({
  session,
  className,
  showControls = true,
  showStats = false
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('https://www.google.com');
  
  useEffect(() => {
    // Simulate connection
    setTimeout(() => setIsConnected(true), 1000);
  }, [session.id]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={clsx(
        'browser-frame relative overflow-hidden h-full',
        'bg-gradient-to-b from-ocean-muted to-ocean-dark',
        className
      )}
    >
      {/* Browser Toolbar */}
      {showControls && (
        <div className="glass px-4 py-2 flex items-center space-x-2 border-b border-ocean-cyan/20">
          <div className="flex space-x-1">
            <button className="p-2 rounded hover:bg-ocean-cyan/20 transition-colors" title="Back">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button className="p-2 rounded hover:bg-ocean-cyan/20 transition-colors" title="Forward">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button className="p-2 rounded hover:bg-ocean-cyan/20 transition-colors" title="Refresh">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          <input
            type="url"
            value={currentUrl}
            onChange={(e) => setCurrentUrl(e.target.value)}
            className="flex-1 px-3 py-1 rounded-lg bg-ocean-deep/50 border border-ocean-cyan/20 
                     text-white placeholder-ocean-cyan/50 focus:outline-none focus:border-ocean-cyan"
            placeholder="Enter URL..."
          />
        </div>
      )}

      {/* Browser Display */}
      <div className="relative bg-black flex-1" style={{ minHeight: '600px' }}>
        {!isConnected ? (
          <div className="absolute inset-0 flex items-center justify-center bg-ocean-deep/90">
            <div className="text-center">
              <div className="w-20 h-20 border-4 border-ocean-cyan border-t-ocean-green 
                            rounded-full animate-spin mx-auto mb-4" />
              <p className="text-xl font-medium text-ocean-cyan">
                Connecting to Browser Container...
              </p>
              <p className="text-sm text-ocean-cyan/70 mt-2">
                Session: {session.id.slice(0, 8)}
              </p>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
            <div className="text-center p-8 max-w-2xl">
              <div className="mb-6">
                <svg className="w-24 h-24 mx-auto text-ocean-cyan" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-ocean-cyan mb-4">
                Browser Container Connected
              </h2>
              
              <div className="bg-ocean-deep/50 rounded-lg p-4 mb-4">
                <p className="text-ocean-green mb-2">✓ Container Ready</p>
                <p className="text-sm text-ocean-cyan/80">
                  Container ID: {session.containerId}
                </p>
                <p className="text-sm text-ocean-cyan/80">
                  Worker URL: {session.workerUrl}
                </p>
              </div>

              <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4">
                <p className="text-yellow-400 font-semibold mb-2">
                  ⚠️ VNC Display Pending
                </p>
                <p className="text-sm text-yellow-300/80">
                  The browser container is running, but noVNC library failed to load.
                  To see the actual browser, we need:
                </p>
                <ul className="text-left text-sm text-yellow-300/70 mt-2 space-y-1">
                  <li>• Real VNC server backend (Neko on a server)</li>
                  <li>• Or wait for Cloudflare Containers public release</li>
                  <li>• Or fix noVNC library loading issue</li>
                </ul>
              </div>
              
              <div className="mt-6 text-xs text-ocean-cyan/50">
                WebSocket: {session.wsUrl}
              </div>
            </div>
          </div>
        )}

        {/* Stats Overlay */}
        {showStats && isConnected && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="absolute top-4 right-4 glass p-3 rounded-lg text-xs font-mono"
          >
            <div className="space-y-1 text-ocean-cyan">
              <div>Status: <span className="text-ocean-green">Connected (Mock)</span></div>
              <div>Session: <span className="text-ocean-green">{session.id.slice(0, 8)}</span></div>
              <div>Container: <span className="text-ocean-green">Ready</span></div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default MockBrowserEmbed;
